import React, { useEffect, useMemo, useRef } from 'react';
import { getDurationSeconds, ParsedReplay, BombPlantEvent } from './replayParser';
import ReplayTimeline, { TimelineEvent } from './ReplayTimeline';
import { REPLAY_SPEEDS } from './useReplayShortcuts';
import type { ReplayLayout } from './ReplayViewer';
import { team1IsCtInRound, teamWonRound } from '../../MatchPage2/matchStats';
import PanToolIcon from '@mui/icons-material/PanTool';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import LinkIcon from '@mui/icons-material/Link';

interface ReplayControlsProps {
    isPlaying: boolean;
    onPlayPause: () => void;
    speed: number;
    onSpeedChange: (speed: number) => void;
    currentFrame: number;
    totalFrames: number;
    onSeek: (frame: number) => void;
    replay: ParsedReplay | null;
    selectedRound: number;
    roundCount: number;
    onRoundChange: (round: number) => void;
    roundEnds?: { [round: number]: { reason: string; teamWonID: string | null } };
    team1Id?: string;
    // Lets a stale round-end winner id be spotted (see teamWonRound).
    team2Id?: string;
    // New props
    timelineEvents?: TimelineEvent[];
    isBombPlanted?: boolean;
    layout?: ReplayLayout;
    onLayoutToggle?: () => void;
    zoom?: number;
    onZoomChange?: (z: number) => void;
    isPanMode?: boolean;
    onPanModeToggle?: () => void;
    onResetView?: () => void;
    isFullscreen?: boolean;
    onFullscreenToggle?: () => void;
    /** map-control overlay toggle, hosted here so it does not cover the map */
    mapControlSupported?: boolean;
    showMapControl?: boolean;
    onMapControlToggle?: () => void;
    mapControlLoading?: boolean;
    /** swing overlay toggle (live round odds + kill feed swing); only passed when the map has the data */
    swingAvailable?: boolean;
    showSwing?: boolean;
    onSwingToggle?: () => void;
    showShortcuts?: boolean;
    onShortcutsToggle?: () => void;
    onCopyLink?: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;

const T_COLOR = '#de9b35';
const CT_COLOR = '#5d79ae';
const T_WIN_REASONS = ['TerroristsWin', 'TargetBombed', 'TerroristsEscaped', 'VipAssassinated'];
const CT_WIN_REASONS = ['CTsWin', 'BombDefused', 'TargetSaved', 'CTsPreventEscape', 'EscapingTerroristsNeutralized', 'VipEscaped', 'AllHostagesRescued', 'HostagesNotRescued', 'TerroristsNotEscaped', 'VipNotEscaped'];

const ReplayControls: React.FC<ReplayControlsProps> = ({
    isPlaying, onPlayPause, speed, onSpeedChange,
    currentFrame, totalFrames, onSeek, replay,
    selectedRound, roundCount, onRoundChange,
    roundEnds, team1Id, team2Id,
    timelineEvents = [], isBombPlanted = false,
    layout = 'split', onLayoutToggle,
    zoom = 1, onZoomChange, isPanMode = false, onPanModeToggle, onResetView,
    isFullscreen = false, onFullscreenToggle,
    mapControlSupported = false, showMapControl = false, onMapControlToggle, mapControlLoading = false,
    swingAvailable = false, showSwing = false, onSwingToggle,
    showShortcuts = false, onShortcutsToggle, onCopyLink,
}) => {
    const tickRate = replay ? Math.max(replay.header.tickRate, 1) : 64;

    // Keep the selected round visible in the scrolling strip; with ↑/↓ the change can come from the
    // keyboard. Only the strip scrolls, never the page, so scrollIntoView is not used.
    const roundStripRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const strip = roundStripRef.current;
        const btn = strip?.querySelector<HTMLElement>(`[data-round="${selectedRound}"]`);
        if (!strip || !btn) return;
        const s = strip.getBoundingClientRect();
        const b = btn.getBoundingClientRect();
        if (b.left < s.left) strip.scrollLeft -= s.left - b.left;
        else if (b.right > s.right) strip.scrollLeft += b.right - s.right;
    }, [selectedRound, roundCount]);

    const bombPlantFrame = useMemo(() => {
        if (!replay) return null;
        const plantEvent = replay.events.find((e): e is BombPlantEvent => e.type === 'bomb_plant');
        return plantEvent ? plantEvent.frameIdx : null;
    }, [replay]);

    const currentTick = replay?.frames[currentFrame]?.tick ?? 0;
    const startTick = replay?.frames[0]?.tick ?? 0;
    const elapsedSecs = (currentTick - startTick) / tickRate;
    const bombPlanted = bombPlantFrame !== null && currentFrame >= bombPlantFrame;

    let displayTime: string;
    if (bombPlanted) {
        const framesSincePlant = currentFrame - bombPlantFrame;
        const secsSincePlant = framesSincePlant / tickRate;
        const remaining = Math.max(0, 40 - secsSincePlant);
        const s = Math.ceil(remaining);
        displayTime = `0:${s.toString().padStart(2, '0')}`;
    } else {
        const remaining = Math.max(0, 115 - elapsedSecs);
        const m = Math.floor(remaining / 60);
        const s = Math.floor(remaining % 60);
        displayTime = `${m}:${s.toString().padStart(2, '0')}`;
    }

    const getRoundWinColor = (round: number): string => {
        if (!roundEnds || !roundEnds[round]) return '#666666';
        const reason = roundEnds[round].reason;
        if (T_WIN_REASONS.includes(reason)) return T_COLOR;
        if (CT_WIN_REASONS.includes(reason)) return CT_COLOR;
        return '#666666';
    };

    // Running scores for round selector
    const runningScores = useMemo(() => {
        if (!roundEnds || !team1Id) return {};
        const scores: { [round: number]: string } = {};
        let t1 = 0, t2 = 0;
        for (let r = 1; r <= roundCount; r++) {
            scores[r] = `${t1}:${t2}`;
            const end = roundEnds[r];
            // teamWonID is empty on uploaded demos, so teamWonRound falls back to the
            // round-end reason (team1 starts CT).
            if (end) {
                if (teamWonRound(end.reason, end.teamWonID, team1Id, team2Id, team1IsCtInRound(r))) t1++;
                else t2++;
            }
        }
        return scores;
    }, [roundEnds, team1Id, team2Id, roundCount]);

    const canPan = zoom > 1;

    const toolBtnStyle = (active = false, disabled = false): React.CSSProperties => ({
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? 'rgba(222,155,53,0.35)' : 'transparent',
        border: active ? '1px solid #de9b35' : '1px solid transparent',
        borderRadius: 3,
        color: disabled ? '#555' : active ? '#f0c060' : '#999',
        cursor: disabled ? 'default' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.4 : 1,
    });

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: '3px',
            backgroundColor: 'var(--bg-primary, #1a1a1a)',
            borderTop: '1px solid var(--border-primary, #333)',
            padding: '5px 10px',
            flexShrink: 0,
        }}>
            {/* Row 1: Playback — play, timeline, timer, speed.
                On mobile the timeline gets its own full-width sub-row so it's actually touchable. */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
            }}>
                {layout === 'mobile' && (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                        <ReplayTimeline
                            currentFrame={currentFrame}
                            totalFrames={totalFrames}
                            onSeek={onSeek}
                            events={timelineEvents}
                            isBombPlanted={isBombPlanted}
                        />
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <button
                        onClick={onPlayPause}
                        title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {isPlaying ? (
                            <svg width="14" height="14" viewBox="0 0 16 16">
                                <rect x="2" y="1" width="4" height="14" rx="1" fill="#de9b35" />
                                <rect x="10" y="1" width="4" height="14" rx="1" fill="#de9b35" />
                            </svg>
                        ) : (
                            <svg width="14" height="14" viewBox="0 0 16 16">
                                <path d="M3 1.5 L14 8 L3 14.5 Z" fill="#de9b35" />
                            </svg>
                        )}
                    </button>

                    {layout !== 'mobile' && (
                        <ReplayTimeline
                            currentFrame={currentFrame}
                            totalFrames={totalFrames}
                            onSeek={onSeek}
                            events={timelineEvents}
                            isBombPlanted={isBombPlanted}
                        />
                    )}

                    <span style={{
                        color: bombPlanted ? '#f0a030' : '#cccccc',
                        fontSize: '12px',
                        fontFamily: '"Consolas", "Menlo", monospace',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        minWidth: '38px',
                        textAlign: 'right',
                        flexShrink: 0,
                        marginLeft: layout === 'mobile' ? 'auto' : undefined,
                    }}>
                        {displayTime}
                    </span>

                    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                        {REPLAY_SPEEDS.map(s => (
                            <button
                                key={s}
                                onClick={() => onSpeedChange(s)}
                                title={`${s}x speed (− / +)`}
                                style={{
                                    background: speed === s ? 'var(--accent-orange, #f15c25)' : 'rgba(255,255,255,0.05)',
                                    border: speed === s ? '1px solid var(--accent-orange, #f15c25)' : '1px solid var(--border-primary, #333)',
                                    color: speed === s ? '#ffffff' : '#888',
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    fontFamily: '"Consolas", "Menlo", monospace',
                                }}
                            >
                                {s}x
                            </button>
                        ))}
                    </div>

                    {mapControlSupported && onMapControlToggle && (
                        <button
                            onClick={onMapControlToggle}
                            style={{
                                ...toolBtnStyle(showMapControl, false),
                                width: 'auto', padding: '0 10px', gap: 6,
                                display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                                fontSize: '0.75rem', fontWeight: 700,
                            }}
                            title="Territory control overlay (alpha)"
                        >
                            Map control
                            <span style={{
                                fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.04em',
                                background: 'rgba(238,125,63,0.22)', border: '1px solid rgba(238,125,63,0.55)',
                                color: '#ee7d3f', borderRadius: 3, padding: '0 3px', lineHeight: '12px',
                            }}>ALPHA</span>
                            {mapControlLoading && <span style={{ opacity: 0.7, fontWeight: 400 }}>…</span>}
                        </button>
                    )}

                    {swingAvailable && onSwingToggle && (
                        <button
                            onClick={onSwingToggle}
                            aria-pressed={showSwing}
                            style={{
                                ...toolBtnStyle(showSwing, false),
                                width: 'auto', padding: '0 10px', gap: 6,
                                display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                                fontSize: '0.75rem', fontWeight: 700,
                            }}
                            title="Live round odds and each kill's swing"
                        >
                            Swing
                        </button>
                    )}

                    {onCopyLink && (
                        <button
                            onClick={onCopyLink}
                            style={toolBtnStyle(false, false)}
                            title="Copy link to this moment (L)"
                        >
                            <LinkIcon style={{ fontSize: 16 }} />
                        </button>
                    )}

                    {/* No keyboard on phones, so no shortcut list there */}
                    {onShortcutsToggle && layout !== 'mobile' && (
                        <button
                            onClick={onShortcutsToggle}
                            style={toolBtnStyle(showShortcuts, false)}
                            title="Keyboard shortcuts (?)"
                        >
                            <KeyboardIcon style={{ fontSize: 16 }} />
                        </button>
                    )}

                    {onFullscreenToggle && (
                        <button
                            onClick={onFullscreenToggle}
                            style={toolBtnStyle(isFullscreen, false)}
                            title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
                        >
                            {isFullscreen
                                ? <FullscreenExitIcon style={{ fontSize: 16 }} />
                                : <FullscreenIcon style={{ fontSize: 16 }} />
                            }
                        </button>
                    )}
                </div>
            </div>

            {/* Row 2: Map tools + Layout toggle + Round selector */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
            }}>
                {/* Map tools — hidden on mobile */}
                {layout !== 'mobile' && (
                    <>
                        {onPanModeToggle && (
                            <button
                                onClick={onPanModeToggle}
                                disabled={!canPan}
                                style={toolBtnStyle(isPanMode && canPan, !canPan)}
                                title={canPan ? 'Pan tool' : 'Zoom in to enable panning'}
                            >
                                <PanToolIcon style={{ fontSize: 14 }} />
                            </button>
                        )}

                        {onZoomChange && (
                            <>
                                <button
                                    onClick={() => onZoomChange(Math.max(MIN_ZOOM, zoom - ZOOM_STEP))}
                                    disabled={zoom <= MIN_ZOOM}
                                    style={toolBtnStyle(false, zoom <= MIN_ZOOM)}
                                    title="Zoom out"
                                >
                                    <RemoveIcon style={{ fontSize: 14 }} />
                                </button>

                                <input
                                    type="range"
                                    min={MIN_ZOOM}
                                    max={MAX_ZOOM}
                                    step={0.1}
                                    value={zoom}
                                    onChange={e => onZoomChange(parseFloat(e.target.value))}
                                    title={`Zoom: ${zoom.toFixed(1)}x`}
                                    style={{
                                        width: 60,
                                        height: 14,
                                        cursor: 'pointer',
                                        accentColor: '#de9b35',
                                    }}
                                />

                                <button
                                    onClick={() => onZoomChange(Math.min(MAX_ZOOM, zoom + ZOOM_STEP))}
                                    disabled={zoom >= MAX_ZOOM}
                                    style={toolBtnStyle(false, zoom >= MAX_ZOOM)}
                                    title="Zoom in"
                                >
                                    <AddIcon style={{ fontSize: 14 }} />
                                </button>

                                <span style={{
                                    color: '#777',
                                    fontSize: 10,
                                    fontFamily: '"Consolas", "Menlo", monospace',
                                    minWidth: 26,
                                    textAlign: 'center',
                                }}>
                                    {zoom.toFixed(1)}x
                                </span>
                            </>
                        )}

                        {onResetView && (
                            <button
                                onClick={onResetView}
                                disabled={zoom === 1}
                                style={toolBtnStyle(false, zoom === 1)}
                                title="Reset view (R)"
                            >
                                <CenterFocusStrongIcon style={{ fontSize: 14 }} />
                            </button>
                        )}

                        {/* Separator */}
                        <div style={{ width: 1, height: 18, backgroundColor: '#444', margin: '0 2px' }} />

                        {/* Layout toggle */}
                        {onLayoutToggle && (
                            <button
                                onClick={onLayoutToggle}
                                style={toolBtnStyle(false, false)}
                                title={layout === 'split' ? 'Switch to stacked layout' : 'Switch to split layout'}
                            >
                                {layout === 'split'
                                    ? <ViewSidebarIcon style={{ fontSize: 14 }} />
                                    : <ViewColumnIcon style={{ fontSize: 14 }} />
                                }
                            </button>
                        )}

                        {/* Separator */}
                        <div style={{ width: 1, height: 18, backgroundColor: '#444', margin: '0 2px' }} />
                    </>
                )}

                {/* Round selector */}
                {roundCount > 1 && (
                    <div ref={roundStripRef} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1px',
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                    }}>
                        {Array.from({ length: roundCount }, (_, i) => {
                            const round = i + 1;
                            const isSelected = round === selectedRound;
                            const winColor = getRoundWinColor(round);
                            const score = runningScores[round];

                            // Half-time separators
                            const showSeparator = round === 12 || round === 24;

                            return (
                                <React.Fragment key={round}>
                                    <button
                                        onClick={() => onRoundChange(round)}
                                        data-round={round}
                                        title={`Round ${round} (↑ / ↓)`}
                                        style={{
                                            background: 'transparent',
                                            border: isSelected
                                                ? '1px solid var(--accent-orange, #f15c25)'
                                                : '1px solid transparent',
                                            color: winColor,
                                            padding: '1px 3px',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            fontSize: '10px',
                                            fontWeight: isSelected ? 700 : 500,
                                            minWidth: '24px',
                                            fontFamily: '"Consolas", "Menlo", monospace',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0,
                                            lineHeight: 1.2,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <span>{round.toString().padStart(2, '0')}</span>
                                        {score && (
                                            <span style={{
                                                fontSize: '7px',
                                                color: '#555',
                                                fontWeight: 400,
                                            }}>
                                                {score}
                                            </span>
                                        )}
                                    </button>
                                    {showSeparator && round < roundCount && (
                                        <div style={{
                                            width: 2,
                                            height: 20,
                                            backgroundColor: 'var(--border-primary, #444)',
                                            margin: '0 3px',
                                            flexShrink: 0,
                                        }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReplayControls;
